import { action, computed, makeObservable, observable } from "mobx";
import { v4 as uuidv4, v4 } from "uuid";

import type {
  ArrayItemsSchema,
  CompositionBranch,
  CompositionKeyword,
  SchemaParameterType,
} from "../types";
import { buildCompositionPropertyJson, checkForJinja, convertParameterToJson } from "../utils";
import { combineValidators, validateRequired } from "../validation";

import type { EnumVariable } from "./types";

export const stringFormats = [
  "date-time",
  "date",
  "time",
  "duration",
  "email",
  "idn-email",
  "hostname",
  "idn-hostname",
  "ipv4",
  "ipv6",
  "uri",
  "uri-reference",
  "iri",
  "iri-reference",
  "uuid",
  "uri-template",
  "json-pointer",
  "relative-json-pointer",
  "regex",
] as const;

export type StringFormat = (typeof stringFormats)[number];

class SchemaParameterStore {
  @observable type: SchemaParameterType = "string";
  @observable name = "";
  @observable title = "";

  @observable _enum: Record<string, string> | undefined = undefined;
  @observable _required: SchemaParameterStore[] = [];
  @observable _collapsed = true;
  @observable enumName: string | undefined;
  @observable isEnumVariable = false;
  @observable variableName: string | undefined;

  // Clean separation of concerns:
  @observable _description = ""; // The description for this parameter
  @observable children: SchemaParameterStore[] = []; // Nested properties (for objects/composition arrays)
  @observable items: ArrayItemsSchema | undefined = undefined; // Items schema (for simple arrays)

  @observable depth = 0;
  private _parent: SchemaParameterStore | undefined = undefined;

  @observable errors: Record<string, string> = {};
  @observable isSubmitAttempted = false;
  @observable nullable = false;

  // String specific
  @observable minLength?: string = undefined;
  @observable maxLength?: string = undefined;
  @observable pattern?: string = undefined;
  @observable format?: string = undefined;

  // Number specific
  @observable minimum?: string = undefined;
  @observable maximum?: string = undefined;
  @observable multipleOf?: string = undefined;
  @observable exclusiveMinimum?: string = undefined;
  @observable exclusiveMaximum?: string = undefined;

  // Array specific
  @observable minItems?: string = undefined;
  @observable maxItems?: string = undefined;

  @observable $ref?: string = undefined;

  /** JSON Schema ``const`` (e.g. discriminant ``"onsite"`` / ``"remote"``). */
  @observable constValue?: string | number | boolean = undefined;

  @observable compositionKeyword?: CompositionKeyword = undefined;
  @observable compositionBranches: CompositionBranch[] = [];

  constructor(depth = 0, collapsed = true, parent?: SchemaParameterStore) {
    this._parent = parent;
    this.depth = depth;
    this._collapsed = collapsed;
    makeObservable(this);
  }

  // Computed description getter - always returns the description
  @computed get description(): string {
    return this._description;
  }

  @action setDescription(value: string) {
    this._description = value;
  }

  @action
  validate(): boolean {
    const validators: Record<string, (value: string | undefined) => string | null> = {
      name: value => validateRequired(value, "Name"),
    };

    // Add variable name validation for variable type
    if (this.type === "variable") {
      validators.variableName = value => validateRequired(value, "Variable name");
    }

    const result = combineValidators(
      { name: this.name, variableName: this.variableName },
      validators,
    );

    this.errors = result.errors;

    let nestedValid = true;
    if (this.children.length > 0) {
      const allResults = this.children.map(param => param.validate());
      nestedValid = allResults.every(res => res === true);
    }

    return result.isValid && nestedValid;
  }

  @action toggleEnumMode() {
    if (this.isEnumVariable) {
      this.enumName = undefined;
    }
    this.isEnumVariable = !this.isEnumVariable;
  }

  @action setEnumName(name: string) {
    this.enumName = name;
  }

  @action setVariableName(name: string) {
    this.variableName = name;
  }

  @action
  markSubmitAttempted() {
    this.isSubmitAttempted = true;
    this.children.forEach(param => param.markSubmitAttempted());
  }

  setDepth(depth: number) {
    this.depth = depth;
    this.children.forEach(param => param.setDepth(depth + 1));
  }

  get isCollapsed() {
    return this._collapsed;
  }

  get isExpanded() {
    return !this._collapsed;
  }

  get isParent() {
    return this.depth === 0;
  }

  get isChild() {
    return !this.isParent;
  }

  @action toggleCollapsed() {
    this._collapsed = !this._collapsed;
  }

  @action toggleRequired() {
    this._parent?.toggleRequiredField(this);
  }

  @action setNullable(value: boolean) {
    this.nullable = value;
  }

  // Simple array = has items schema (not children)
  // Composition array = has children (nested object properties)
  get isSimpleArray(): boolean {
    return this.type === "array" && this.items !== undefined;
  }

  get isCompositionArray(): boolean {
    return this.type === "array" && this.items !== undefined;
  }

  @action toggleArrayType() {
    if (this.isSimpleArray) {
      // Switch from simple to composition array
      this.items = undefined;
      this.children = [new SchemaParameterStore(this.depth + 1, this._collapsed, this)];
    } else {
      // Switch from composition to simple array
      this.children = [];
      this._required = [];
      this.items = {
        type: "string",
        description: "",
      };
    }
  }

  @action
  toggleRequiredField(value: SchemaParameterStore) {
    if (this._required.includes(value)) {
      const idx = this._required.indexOf(value);
      if (idx !== undefined && idx >= 0) {
        this._required.splice(idx, 1);
      }
    } else {
      this._required.push(value);
    }
  }

  get required() {
    return !!this._parent?._required?.includes(this);
  }

  @action
  setType(type: SchemaParameterType) {
    this.type = type;
    if (["array", "object"].includes(type)) {
      // Default to composition (nested properties) for both
      this.children = [new SchemaParameterStore(this.depth + 1, this._collapsed, this)];
      this.items = undefined;
    } else {
      // Clear nested structures for simple types
      this._required = [];
      this.children = [];
      this.items = undefined;
    }

    // Clear variable name if switching away from variable type
    if (type !== "variable") {
      this.variableName = undefined;
    }

    this._enum = undefined;
    this.enumName = undefined;
    this.isEnumVariable = false;
  }

  get isEditableList(): boolean {
    return (this.type === "array" && this.children.length > 0) || this.type === "object";
  }

  get hasNestedContent(): boolean {
    return this.children.length > 0 || this.items !== undefined;
  }

  @action
  addField() {
    if (this.children.length > 0) {
      this.children.push(new SchemaParameterStore(this.depth + 1, false, this));
      this.setDepth(this.depth);
    } else if (["object", "array"].includes(this.type)) {
      this.children = [new SchemaParameterStore(this.depth + 1, false, this)];
      this.items = undefined;
    }
  }

  @action setTitle(title: string) {
    this.title = title;
  }

  @action setName(name: string) {
    this.name = name;
  }

  @computed get metadata() {
    return {
      name: this.name,
      type: this.type,
      title: this.title,
      required: this.required,
      nullable: this.nullable,
    };
  }

  @computed get hasEnums() {
    return this.enum !== undefined;
  }

  @computed get enum() {
    return this._enum ? Object.values(this._enum) : undefined;
  }

  @action setEnums(enums: string[] | EnumVariable) {
    if (!Array.isArray(enums) && typeof enums === "object" && enums.type === "enum_variable") {
      this.enumName = enums.name;
      this.isEnumVariable = true;
      this._enum = { [v4()]: "" };
    } else if (Array.isArray(enums)) {
      this.isEnumVariable = false;
      this.enumName = undefined;
      this._enum = enums.reduce(
        (acc, elem) => {
          acc[uuidv4()] = elem;
          return acc;
        },
        {} as Record<string, string>,
      );
    }
  }

  @computed get enumsAvailable() {
    return this.type === "string" || this.type === "number";
  }

  @action addEnum() {
    if (!this._enum) {
      this._enum = {};
    }
    this._enum[uuidv4()] = "";
  }

  @action toggleEnums() {
    if (this.hasEnums) {
      this._enum = undefined;
    } else {
      this.addEnum();
    }
  }

  @action setItems(items: ArrayItemsSchema) {
    this.items = items;
  }

  @action setChildren(children: SchemaParameterStore[]) {
    this.children = children;
  }

  @action deleteEnum(key: string) {
    if (!this._enum) {
      this._enum = {};
    }
    delete this._enum[key];
  }

  @action setEnum(key: string, value: string) {
    if (!this._enum) {
      this._enum = {};
    }
    this._enum[key] = value;
  }

  @action setRequired(parameters: SchemaParameterStore[]) {
    this._required = parameters;
  }

  @computed get hasComposition() {
    return Boolean(this.compositionKeyword && this.compositionBranches.length > 0);
  }

  @computed get schema() {
    const param = this as ISchemaParameter;

    // Handle variable type
    if (param.type === "variable") {
      return {
        type: "variable",
        name: param.variableName || "",
      };
    }

    if (param.hasComposition) {
      return buildCompositionPropertyJson(param);
    }

    if (param.constValue !== undefined) {
      const constSchema: Record<string, unknown> = { const: param.constValue };
      if (param.description) {
        constSchema.description = param.description;
      }
      if (param.title) {
        constSchema.title = param.title;
      }
      return constSchema;
    }

    // Only include required if it has values
    const requiredNames = param._required?.map(p => p?.name || "invalid_reference");
    const required = requiredNames && requiredNames.length > 0 ? requiredNames : undefined;

    // $ref cannot have sibling keywords per JSON Schema spec.
    // Azure OpenAI strictly enforces this and rejects schemas that include
    // type/description/etc. alongside $ref.
    if (param.$ref) {
      return { $ref: param.$ref };
    }

    const baseObj = {
      type: param.nullable ? ([param.type, "null"] as [string, "null"]) : param.type,
      description: param.description || undefined,
      title: param.title || undefined,
      required,
      enum: param.enum,
      minLength: param.minLength,
      maxLength: param.maxLength,
      pattern: param.pattern,
      format: param.format,
      minimum: param.minimum,
      maximum: param.maximum,
      multipleOf: param.multipleOf,
      exclusiveMinimum: param.exclusiveMinimum,
      exclusiveMaximum: param.exclusiveMaximum,
      minItems: param.minItems,
      maxItems: param.maxItems,
    };

    // Object with nested properties
    if (param.type === "object" && param.children.length > 0) {
      const properties: Record<string, object> = {};

      param.children.forEach(childParam => {
        properties[childParam.name] = {
          ...convertParameterToJson(childParam),
          name: undefined,
        };
      });

      return {
        ...baseObj,
        additionalProperties: false,
        properties,
      };
    }

    // Composition array (array of objects with properties)
    if (param.type === "array" && param.children.length > 0) {
      return {
        ...baseObj,
        required: undefined,
        items: {
          type: "object",
          required: baseObj.required || [],
          additionalProperties: false,
          properties: param.children
            .map(childParam => convertParameterToJson(childParam))
            .reduce(
              (acc, elem) => ({
                ...acc,
                [elem.name]: { ...elem, name: undefined },
              }),
              {},
            ),
        },
      };
    }

    // Simple array (array of primitives)
    if (param.type === "array" && param.items) {
      return {
        ...baseObj,
        required: undefined,
        items: {
          ...param.items,
        },
      };
    }

    if (param.hasEnums) {
      if (param.isEnumVariable) {
        return {
          ...baseObj,
          enum: {
            name: param.enumName ?? "",
            type: "enum_variable",
          },
        };
      }

      return {
        ...baseObj,
        enum: param.enum,
      };
    }

    return baseObj;
  }

  @computed get containsJinja(): boolean {
    if (checkForJinja(this.title) || checkForJinja(this.name)) {
      return true;
    }

    if (checkForJinja(this._description)) {
      return true;
    }

    if (this.children.length > 0) {
      return this.children.some(param => param.containsJinja);
    }

    if (this.items && typeof this.items.description === "string") {
      return checkForJinja(this.items.description);
    }

    return false;
  }

  @action setMinLength(minLength: string) {
    this.minLength = this.setStringValue(minLength);
  }

  @action setMaxLength(maxLength: string) {
    this.maxLength = this.setStringValue(maxLength);
  }

  @action setPattern(pattern: string) {
    this.pattern = this.setStringValue(pattern);
  }

  @action setFormat(format: string) {
    this.format = this.setStringValue(format);
  }

  @action setMinimum(value: string) {
    this.minimum = this.setStringValue(value);
  }

  @action setMaximum(value: string) {
    this.maximum = this.setStringValue(value);
  }

  @action setMultipleOf(value: string) {
    this.multipleOf = this.setStringValue(value);
  }

  @action setExclusiveMinimum(value: string) {
    this.exclusiveMinimum = this.setStringValue(value);
  }

  @action setExclusiveMaximum(value: string) {
    this.exclusiveMaximum = this.setStringValue(value);
  }

  @action setMinItems(value: string) {
    this.minItems = this.setStringValue(value);
  }

  @action setMaxItems(value: string) {
    this.maxItems = this.setStringValue(value);
  }

  @action setRef(value: string) {
    this.$ref = this.setStringValue(value);
  }

  private setStringValue(value: string) {
    if (value.trim() === "") {
      return undefined;
    }
    // Try to parse as JSON
    // This will convert numbers like "5" to 5
    // If parsing fails, return the original string
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
}

export type ISchemaParameter = InstanceType<typeof SchemaParameterStore>;

export default SchemaParameterStore;
